import React from 'react';
import { GeneratedSlide } from './types';
import { MediaRole, normalizeMediaRole } from '../MediaRole';
import { EffectType } from '../effects';
import { ObjectFit } from '../types/style';
import {
    SerializableState,
    GeneratedSlideState,
    Serializable
} from '../types/serializable';
import { validateMediaState } from '../schemas/timeline-schema';
import { deserializeEffects, serializeEffects } from '../effects/serialization';

export class GeneratedSlideImpl implements GeneratedSlide, Serializable {
    id: string;
    type: 'generated';
    role: MediaRole;
    content: Record<string, unknown>;
    effects: EffectType[];

    constructor(
        id: string,
        role: MediaRole,
        content: Record<string, unknown>,
        effects: EffectType[] = []
    ) {
        this.id = id;
        this.type = 'generated';
        this.role = role;
        this.content = content;
        this.effects = effects;
    }

    /**
     * Export GeneratedSlide to serializable state
     */
    exportState(): GeneratedSlideState {
        const state: GeneratedSlideState = {
            type: 'GeneratedSlide',
            version: '1.0.0',
            id: this.id,
            content: this.content
        };

        // Add optional properties
        if (this.role !== undefined) state.role = this.role;
        if (this.effects !== undefined && this.effects.length > 0) {
            state.effects = serializeEffects(this.effects);
        }

        return state;
    }

    /**
     * Import GeneratedSlide from serialized state
     */
    static importState(state: SerializableState): GeneratedSlideImpl {
        const validatedState = validateMediaState(state);
        if (validatedState.type !== 'GeneratedSlide') {
            throw new Error(`Expected GeneratedSlide state, received ${validatedState.type}`);
        }

        return new GeneratedSlideImpl(
            validatedState.id,
            normalizeMediaRole(validatedState.role),
            validatedState.content,
            deserializeEffects(validatedState.effects)
        );
    }

    render(fps: number, blockId: string, duration: number, objectFit?: ObjectFit, videoConfig?: { width: number; height: number }) {
        if (!videoConfig) {
            // For GeneratedSlide, we can provide a fallback since it doesn't strictly need video dimensions
            console.warn('GeneratedSlideImpl.render: videoConfig not provided, using defaults');
        }
        const backgroundColor = typeof this.content.backgroundColor === 'string' ? this.content.backgroundColor : 'transparent';
        const textColor = typeof this.content.textColor === 'string' ? this.content.textColor : 'black';
        const fontSize = typeof this.content.fontSize === 'string' || typeof this.content.fontSize === 'number'
            ? this.content.fontSize
            : '24px';
        const padding = typeof this.content.padding === 'string' || typeof this.content.padding === 'number'
            ? this.content.padding
            : '20px';
        const text = typeof this.content.text === 'string' || typeof this.content.text === 'number'
            ? this.content.text
            : '';

        return (
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor,
                    color: textColor,
                    fontSize,
                    padding,
                }}
            >
                {text}
            </div>
        );
    }
}
